/**
 * ManageAssignmentsDialog — Phase 86.D.
 *
 * Two jobs in one place, because they answer the same question from opposite
 * ends:
 *   1. "What is this account FOR?"     — purpose tags
 *   2. "What money funds this goal?"   — per-amount allocations
 *
 * Live over-allocation feedback is the point. As soon as the amounts promised
 * from one balance exceed what it holds, the row says so — before saving, not
 * after — so two goals can never quietly both look funded from the same money.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatMoney } from '@/lib/format';
import {
  ALLOCATION_METHODS, PURPOSE_OPTIONS, fetchAllocationResources,
  fetchGoalAllocations, replaceGoalAllocations, setAccountPurpose,
} from '@/lib/allocationsApi';

const KIND_FOR_TABLE = {
  accounts: 'account',
  wealth_holdings: 'wealth_holding',
  liabilities: 'liability',
};

export default function ManageAssignmentsDialog({ open, onOpenChange, accounts = [], onSaved }) {
  const [resources, setResources] = useState([]);
  const [goals, setGoals]         = useState([]);
  const [purposes, setPurposes]   = useState({});
  const [draft, setDraft]         = useState({});   // goalId -> [{resource_id, method, value}]
  const [activeGoal, setActiveGoal] = useState('');
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [migrationPending, setMigrationPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [res, ga] = await Promise.all([fetchAllocationResources(), fetchGoalAllocations()]);
      if (cancelled) return;
      setResources(res?.resources || []);
      setGoals(ga?.goals || []);
      setActiveGoal((prev) => prev || ga?.goals?.[0]?.goal_id || '');
      const d = {};
      (ga?.goals || []).forEach((g) => {
        d[g.goal_id] = (g.resources || []).map((r) => ({
          resource_id: r.resource_id,
          method: r.allocation_method || 'entire_resource',
          value: r.allocation_value ?? '',
        }));
      });
      setDraft(d);
      const p = {};
      accounts.forEach((a) => { if (a.raw?.purpose) p[a.id] = a.raw.purpose; });
      setPurposes(p);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resourceById = useMemo(
    () => new Map(resources.map((r) => [r.resource_id, r])), [resources],
  );

  /** What every balance has been promised across ALL goals in the current draft. */
  const claims = useMemo(() => {
    const m = new Map();
    Object.values(draft).forEach((list) => {
      list.forEach((a) => {
        const r = resourceById.get(a.resource_id);
        if (!r || r.side !== 'asset') return;
        let amt = r.value;
        if (a.method === 'percentage') amt = r.value * ((Number(a.value) || 0) / 100);
        else if (a.method === 'fixed_amount') amt = Number(a.value) || 0;
        m.set(a.resource_id, (m.get(a.resource_id) || 0) + amt);
      });
    });
    return m;
  }, [draft, resourceById]);

  const overAllocated = useMemo(() => resources.filter(
    (r) => r.side === 'asset' && (claims.get(r.resource_id) || 0) - r.value > 0.01,
  ), [resources, claims]);

  const cashAccounts = accounts.filter((a) => a.category === 'cash');
  const currentList = draft[activeGoal] || [];

  const toggle = (resourceId) => {
    setDraft((d) => {
      const list = d[activeGoal] || [];
      const exists = list.some((a) => a.resource_id === resourceId);
      return {
        ...d,
        [activeGoal]: exists
          ? list.filter((a) => a.resource_id !== resourceId)
          : [...list, { resource_id: resourceId, method: 'entire_resource', value: '' }],
      };
    });
  };

  const update = (resourceId, patch) => {
    setDraft((d) => ({
      ...d,
      [activeGoal]: (d[activeGoal] || []).map(
        (a) => (a.resource_id === resourceId ? { ...a, ...patch } : a)),
    }));
  };

  const savePurpose = async (accountId, purpose) => {
    setPurposes((p) => ({ ...p, [accountId]: purpose }));
    const res = await setAccountPurpose(accountId, purpose || null);
    if (!res.ok) {
      if (res.migrationPending) {
        setMigrationPending(true);
        toast.error('Purpose tags need the Phase 86.D migration applied first.');
      } else {
        toast.error(`Couldn't save purpose: ${res.error}`);
      }
    }
  };

  const saveAllocations = async () => {
    setSaving(true);
    let failed = 0;
    for (const [goalId, list] of Object.entries(draft)) {
      const payload = list.map((a) => {
        const r = resourceById.get(a.resource_id);
        return {
          instrument_kind: KIND_FOR_TABLE[r?.source_table] || 'account',
          instrument_id: a.resource_id,
          allocation_method: a.method,
          allocation_value: a.method === 'entire_resource' ? null : Number(a.value) || 0,
        };
      });
      const res = await replaceGoalAllocations(goalId, payload);
      if (!res.ok) {
        failed += 1;
        if (res.migrationPending) setMigrationPending(true);
      }
    }
    setSaving(false);
    if (failed === 0) {
      toast.success('Assignments saved.');
      onSaved?.();
      onOpenChange(false);
    } else if (!migrationPending) {
      toast.error(`${failed} goal${failed === 1 ? '' : 's'} failed to save.`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="manage-assignments-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-medium">Manage assignments</DialogTitle>
          <DialogDescription>
            Tag what each account is for, and assign specific amounts to your goals.
          </DialogDescription>
        </DialogHeader>

        {migrationPending && (
          <div className="rounded-lg border px-3.5 py-3 flex items-start gap-2.5"
               style={{ borderColor: '#B0721040', backgroundColor: '#B072100F' }}
               data-testid="assignments-migration-warning">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#8A5A0C' }} />
            <p className="text-xs text-charcoal leading-relaxed">
              <span className="font-semibold">Database migration pending.</span>{' '}
              Run <code className="text-[11px]">schema_phase86d_purpose_allocations.sql</code> in
              Supabase to enable purpose tags and per-amount allocations. Your
              existing goal links are unaffected.
            </p>
          </div>
        )}

        {loading ? (
          <div className="py-10 grid place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* 1. Purpose */}
            <section className="mt-1">
              <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground border-b border-border pb-2">
                What each account is for
              </h4>
              <ul className="divide-y divide-border/40" data-testid="assignments-purposes">
                {cashAccounts.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-4 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm text-charcoal truncate">{a.name}</p>
                      <p className="num text-[11px] text-muted-foreground">
                        {formatMoney(Math.abs(a.balance || 0), a.currency)}
                      </p>
                    </div>
                    <Select
                      value={purposes[a.id] || ''}
                      onValueChange={(v) => savePurpose(a.id, v)}
                    >
                      <SelectTrigger className="w-48 h-9 text-xs" data-testid={`purpose-select-${a.id}`}>
                        <SelectValue placeholder="Not set" />
                      </SelectTrigger>
                      <SelectContent>
                        {PURPOSE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value} className="text-xs">
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </li>
                ))}
              </ul>
            </section>

            {/* 2. Goal allocations */}
            <section className="mt-6">
              <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground border-b border-border pb-2">
                Goal assignments
              </h4>

              {goals.length === 0 ? (
                <p className="text-xs text-muted-foreground py-3">
                  Create a goal first, then you can assign money to it.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-1.5 my-3" data-testid="assignments-goal-tabs">
                    {goals.map((g) => (
                      <button
                        key={g.goal_id}
                        type="button"
                        onClick={() => setActiveGoal(g.goal_id)}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                          activeGoal === g.goal_id
                            ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                            : 'bg-muted/40 text-muted-foreground hover:bg-muted/70'}`}
                        data-testid={`assignments-goal-tab-${g.goal_id}`}
                      >
                        {g.title}
                      </button>
                    ))}
                  </div>

                  <ul className="divide-y divide-border/40">
                    {resources.filter((r) => r.side === 'asset' || r.kind).map((r) => {
                      const picked = currentList.find((a) => a.resource_id === r.resource_id);
                      const promised = claims.get(r.resource_id) || 0;
                      const isOver = r.side === 'asset' && promised - r.value > 0.01;
                      return (
                        <li key={r.resource_id} className="py-2.5" data-testid={`assign-row-${r.resource_id}`}>
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={!!picked}
                              onChange={() => toggle(r.resource_id)}
                              className="h-4 w-4 rounded border-border accent-[hsl(var(--primary))]"
                              data-testid={`assign-check-${r.resource_id}`}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-charcoal truncate">{r.name}</p>
                              <p className="num text-[11px] text-muted-foreground">
                                {formatMoney(r.value, r.currency)}
                                {r.side === 'liability' && ' owed'}
                                {r.side === 'asset' && promised > 0 && (
                                  <> · {formatMoney(promised, r.currency)} assigned</>
                                )}
                              </p>
                            </div>
                            {picked && r.side === 'asset' && (
                              <>
                                <Select
                                  value={picked.method}
                                  onValueChange={(v) => update(r.resource_id, { method: v })}
                                >
                                  <SelectTrigger className="w-36 h-9 text-xs"
                                                 data-testid={`assign-method-${r.resource_id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ALLOCATION_METHODS.map((m) => (
                                      <SelectItem key={m.value} value={m.value} className="text-xs">
                                        {m.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {picked.method !== 'entire_resource' && (
                                  <Input
                                    type="number"
                                    min="0"
                                    value={picked.value}
                                    onChange={(e) => update(r.resource_id, { value: e.target.value })}
                                    placeholder={picked.method === 'percentage' ? '%' : 'amount'}
                                    className="w-24 h-9 text-xs"
                                    data-testid={`assign-value-${r.resource_id}`}
                                  />
                                )}
                              </>
                            )}
                          </div>
                          {isOver && (
                            <p className="text-[11px] mt-1.5 ml-7" style={{ color: '#A84E38' }}
                               data-testid={`assign-over-${r.resource_id}`}>
                              Promised {formatMoney(promised, r.currency)} but only{' '}
                              {formatMoney(r.value, r.currency)} available — over by{' '}
                              {formatMoney(promised - r.value, r.currency)}.
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </section>

            <footer className="flex items-center justify-between gap-4 mt-6 pt-4 border-t border-border">
              <p className="text-[11px] text-muted-foreground">
                {overAllocated.length > 0
                  ? `${overAllocated.length} balance${overAllocated.length === 1 ? '' : 's'} over-assigned. You can still save — we'll keep flagging it.`
                  : 'Purpose changes save immediately; goal assignments save on Done.'}
              </p>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}
                        data-testid="assignments-cancel">
                  Cancel
                </Button>
                <Button size="sm" onClick={saveAllocations} disabled={saving || goals.length === 0}
                        data-testid="assignments-save">
                  {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                  Done
                </Button>
              </div>
            </footer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
